/**
 * Loads `GET /credit-limits/config` for the «Approval rules» tab (B5) and keeps it current after a save: the answer of
 * every `PUT config/*` is the whole configuration again, so the tab never needs a second request. A failed load is
 * reported, never shown as an empty list (CLAUDE.md, learning "an empty dropdown can be a 500").
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { creditLimitsApi } from '../../../services/creditLimitsApi';
import type { CreditLimitConfig, CreditLimitConfigWriteResult } from '../../../types/creditLimits';
import { apiErrorText } from '../requests/requestRules';

export interface UseCreditLimitConfig {
    config: CreditLimitConfig | null;
    loading: boolean;
    error: string | null;
    reload: () => void;
    /**
     * Runs one save. Resolves with the answer (and shows it), rejects with the API's error so the caller can put it
     * next to its form (status on the error: 400 value, 403 not Finance, 409 conflict).
     */
    save: (write: () => Promise<CreditLimitConfigWriteResult>) => Promise<CreditLimitConfigWriteResult>;
}

export function useCreditLimitConfig(): UseCreditLimitConfig {
    const [config, setConfig] = useState<CreditLimitConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [trigger, setTrigger] = useState(0);
    const counter = useRef(0);

    useEffect(() => {
        const mine = ++counter.current;
        setLoading(true);
        setError(null);
        creditLimitsApi.getConfig()
            .then(c => { if (mine === counter.current) setConfig(c); })
            .catch(err => { if (mine === counter.current) setError(apiErrorText(err, 'Failed to load the approval rules')); })
            .finally(() => { if (mine === counter.current) setLoading(false); });
    }, [trigger]);

    const reload = useCallback(() => setTrigger(n => n + 1), []);

    const save = useCallback(async (write: () => Promise<CreditLimitConfigWriteResult>) => {
        const result = await write();
        // The save is committed, but the answer carries no configuration (read-back failed, B6b): keep what is shown
        // and load it again, never blank the tab.
        if (!Array.isArray(result?.tiers)) {
            setTrigger(n => n + 1);
            return result;
        }
        // A reload that was still running must not overwrite this newer answer.
        counter.current++;
        setConfig(result);
        setError(null);
        setLoading(false);
        return result;
    }, []);

    return { config, loading, error, reload, save };
}
